import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';
import { getLocale } from '../i18n';
import * as networking from './networking.js';

export const AUTH_BASE_PATH = '/api/auth';
export const AUTH_ACCEPT_PATH = `${AUTH_BASE_PATH}/invitation`;
export const AUTH_CREATE_END_POINT = AUTH_BASE_PATH + '/sign_in';
export const AUTH_DESTROY_END_POINT = AUTH_BASE_PATH + '/sign_out';
export const AUTH_PASSWORD_END_POINT = AUTH_BASE_PATH + '/password';
export const AUTH_VALIDATE_TOKEN_END_POINT = AUTH_BASE_PATH + '/validate_token';
export const AUTH_SESSION_KEY = ['session'];

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

let baseOwnerHeader: string | undefined;
let pageViewHeaderSource: (() => string | undefined) | undefined;

// The organization the UI is scoped to, for attribution only: the server
// validates it against the user's roles before believing it. Held here rather
// than React state because requests are made from plain modules.
export const setBaseOwnerHeader = (id?: string | number | null) => {
  baseOwnerHeader = id === undefined || id === null ? undefined : String(id);
};

// The screen render an API call belongs to, so a server-side event joins the
// browser's page view. A getter rather than a value: the id changes on every
// navigation and the analytics client owns it.
export const setPageViewHeaderSource = (source?: () => string | undefined) => {
  pageViewHeaderSource = source;
};

export const buildRequestHeaders = (headers = {}) => {
  const pageView = pageViewHeaderSource?.();
  return {
    ...DEFAULT_HEADERS,
    'X-Rhino-Locale': getLocale(),
    ...(baseOwnerHeader ? { 'X-Rhino-Base-Owner': baseOwnerHeader } : {}),
    ...(pageView ? { 'Rhino-Page-View': pageView } : {}),
    ...headers
  };
};

const _buildHeaders = buildRequestHeaders;

export class NetworkUnauthorizedError extends Error {
  constructor(message?: string) {
    super(message);

    this.name = 'NetworkUnauthorizedError';
  }
}

export class NetworkParamError extends Error {
  // FIXME: more specific type
  public errors: string[] | Record<string, string[]>;
  public status?: number;

  constructor(
    errors: string[] | Record<string, string[]>,
    status?: number,
    message?: string
  ) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(message);

    this.name = 'NetworkParamError';
    this.errors = errors;
    this.status = status;
  }
}

export class NetworkServerError extends Error {
  public status: number;

  constructor(status: number, message?: string) {
    super(message);

    this.name = 'NetworkServerError';
    this.status = status;
  }
}

export const networkApiCall = (path: string, options?: AxiosRequestConfig) => {
  const defaultOptions = {
    method: 'get',
    headers: {},
    data: null,
    signal: null,
    ...options
  } as AxiosRequestConfig;

  return axios(path, {
    ...defaultOptions,
    headers: _buildHeaders(defaultOptions.headers),
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
    },
    withCredentials: true
  }).catch((error) => {
    if (
      ((error.response.status === 401 || error.response.status === 403) &&
        (!path.startsWith(AUTH_BASE_PATH) ||
          path === AUTH_VALIDATE_TOKEN_END_POINT)) ||
      (error.response.status === 404 && path === AUTH_DESTROY_END_POINT)
    ) {
      // If the response is 401 or 403 (and not part of authenticating (other
      // than token validation), invalidate the session
      throw new NetworkUnauthorizedError();
    } else if (error.response.status >= 500) {
      throw new NetworkServerError(error.response.status);
    } else if (error.response.data?.errors) {
      // If its a "regular" error, pass the messages back to the user
      throw new NetworkParamError(
        error.response.data?.errors,
        error.response.status
      );
    }
  });
};

export const networkApiCallOnlyData = async (
  path: string,
  options = {} as AxiosRequestConfig
) => {
  // Will have data if no error
  const response = (await networking.networkApiCall(path, options)) as {
    data: unknown;
  };

  return response.data;
};
